import ChartContainer from "components/molecules/ChartContainer";
import { AreaChart } from "components/molecules/Charts";
import { AreaChartDatum } from "components/molecules/Charts/AreaChart";
import { ChartNoDataState } from "components/molecules/NoDataState";
import { DATA_KEY_COLORS } from "constants/chartsColors.constant";
import { useMemo, useState } from "react";
import usePlansState from "recoilStore/Building/usePlansState";
import { getDisplayInfo } from "utils/formatting";
import prepareCostsCardData from "../CostsCard.helper";

function CostsChart() {
    const { referencePlan, comparedPlan, referencePlanName, comparedPlanName, referencedPlanId, comparedPlanId } =
        usePlansState();
    const { chartCostsData } = prepareCostsCardData(referencePlan, comparedPlan, referencePlanName, comparedPlanName);

    const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set());

    const allChartData = useMemo((): AreaChartDatum[] => {
        if (!chartCostsData || chartCostsData?.length === 0) return [];

        const totalCostReferenceData: { x: number; y: number }[] = [];
        const totalCostComparedData: { x: number; y: number }[] = [];

        chartCostsData.forEach(({ year: x, totalCostReference, totalCostCompared }) => {
            totalCostComparedData.push({
                x,
                y: totalCostCompared || 0,
            });
            totalCostReferenceData.push({
                x,
                y: totalCostReference || 0,
            });
        });

        return [
            {
                type: "fullLine" as const,
                id: `${referencePlanName} total cost`,
                color: DATA_KEY_COLORS.recommendations,
                data: totalCostReferenceData,
                formatKey: "netTotalCost",
            },

            ...(comparedPlanId !== referencedPlanId
                ? [
                      {
                          type: "fullLine" as const,
                          id: `${comparedPlanName} total Cost`,
                          color: DATA_KEY_COLORS.baseline,
                          data: totalCostComparedData,
                          formatKey: "netTotalCost",
                      },
                  ]
                : []),
        ];
    }, [chartCostsData, referencePlanName, comparedPlanName, comparedPlanId, referencedPlanId]);

    // This allows clicking on the legend to hide line(s)
    const visibleChartData = allChartData.filter((item) => !hiddenLines.has(item.id));

    const handleLegendClick = (lineId: string) => {
        setHiddenLines((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(lineId)) {
                newSet.delete(lineId);
            } else {
                newSet.add(lineId);
            }
            return newSet;
        });
    };

    const { unit, formatFunction } = getDisplayInfo(
        "netTotalCost",
        visibleChartData.length > 0 ? visibleChartData[0].data.map(({ y }) => y) : []
    );

    if (chartCostsData === null) return <ChartNoDataState title="Annual Carbon Emissions" />;

    return (
        <ChartContainer title="Costs" exportableData={chartCostsData} hideTitle={true}>
            <AreaChart
                data={visibleChartData}
                allData={allChartData}
                hiddenLines={hiddenLines}
                onLegendClick={handleLegendClick}
                yAxisInfo={{
                    formatFunction: (v: string | number) => formatFunction(v, false),
                    unit: unit || "",
                }}
                enableGridX={true}
                height={350}
            />
        </ChartContainer>
    );
}

export default CostsChart;
